import React from 'react';
import {
  TextField, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel, Box, Typography
} from '@mui/material';

/**
 * Renders the appropriate input widget for a custom field definition.
 *
 * Props:
 *   field   — custom field definition { id, name, field_type, options, required }
 *   value   — current value (string|bool)
 *   onChange — (fieldId, value) => void
 *   readOnly — bool (optional)
 */
export default function CustomFieldInput({ field, value, onChange, readOnly }) {
  const handleChange = (val) => !readOnly && onChange(field.id, val);

  switch (field.field_type) {
    case 'text':
      return (
        <TextField
          fullWidth
          size="small"
          label={field.name}
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          required={!!field.required}
          InputProps={{ readOnly }}
        />
      );

    case 'number':
      return (
        <TextField
          fullWidth
          size="small"
          label={field.name}
          type="number"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          required={!!field.required}
          InputProps={{ readOnly }}
        />
      );

    case 'date':
      return (
        <TextField
          fullWidth
          size="small"
          label={field.name}
          type="date"
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
          required={!!field.required}
          InputLabelProps={{ shrink: true }}
          InputProps={{ readOnly }}
        />
      );

    case 'select': {
      const options = Array.isArray(field.options) ? field.options : [];
      return (
        <FormControl fullWidth size="small" required={!!field.required}>
          <InputLabel>{field.name}</InputLabel>
          <Select
            label={field.name}
            value={value || ''}
            onChange={e => handleChange(e.target.value)}
            inputProps={{ readOnly }}
          >
            <MenuItem value=""><em>— aucun —</em></MenuItem>
            {options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={value === 'true' || value === true}
              onChange={e => handleChange(String(e.target.checked))}
              disabled={readOnly}
              size="small"
            />
          }
          label={<Typography variant="body2">{field.name}{field.required ? ' *' : ''}</Typography>}
        />
      );

    default:
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">{field.name}: {value || '—'}</Typography>
        </Box>
      );
  }
}
